import { useMemo, useRef, useState } from 'react';
import { Bookmark, BookmarkCheck, ChevronDown, Share2, Star, Trash2 } from 'lucide-react';
import { Button } from './button';
import { cn } from '@/lib/utils';
import {
  AppModal,
  AppModalHeader,
  AppModalTitle,
  AppModalBody,
  AppModalFooter,
} from './AppModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu';
import type { SaveGridViewRequest, SavedGridView } from '../../types/gridView';

interface GridViewsDropdownProps {
  views: SavedGridView[];
  onApplyView: (view: SavedGridView) => void;
  onSaveView: (request: Omit<SaveGridViewRequest, 'programId' | 'gridKey' | 'tablePreference' | 'filters' | 'viewMode'>) => void;
  onDeleteView: (id: string) => void | Promise<unknown>;
  onSetDefault: (id: string) => void | Promise<unknown>;
  className?: string;
  shareEnabled?: boolean;
}

function SaveGridViewDialog({
  open,
  onOpenChange,
  onSave,
  shareEnabled,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (request: Omit<SaveGridViewRequest, 'programId' | 'gridKey' | 'tablePreference' | 'filters' | 'viewMode'>) => void;
  shareEnabled: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [isShared, setIsShared] = useState(false);
  const [roleCode, setRoleCode] = useState('');

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }

    onSave({
      name: trimmed,
      description: description.trim() || null,
      isDefault,
      isShared: shareEnabled ? isShared : false,
      roleCode: shareEnabled && roleCode.trim() ? roleCode.trim() : null,
      userId: null,
    });

    setName('');
    setDescription('');
    setIsDefault(false);
    setIsShared(false);
    setRoleCode('');
    onOpenChange(false);
  };

  return (
    <AppModal open={open} onClose={() => onOpenChange(false)} size="sm">
      <AppModalHeader>
        <AppModalTitle className="text-[14px] font-semibold">Save view</AppModalTitle>
      </AppModalHeader>
      <AppModalBody>
        <div className="space-y-3 py-1">
          <div>
            <label className="mb-1 block text-[11px] text-muted-foreground">View name</label>
            <input
              ref={inputRef}
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && handleSave()}
              className="w-full rounded-md border border-border px-2 py-1.5 text-[12px] outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              placeholder="e.g. Daily active cases"
            />
          </div>
          <div>
            <label className="mb-1 block text-[11px] text-muted-foreground">Description</label>
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
              className="w-full resize-none rounded-md border border-border px-2 py-1.5 text-[12px] outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              placeholder="Optional description"
            />
          </div>
          <label className="flex items-center gap-2 text-[12px]">
            <input type="checkbox" checked={isDefault} onChange={(event) => setIsDefault(event.target.checked)} className="h-3.5 w-3.5 accent-primary" />
            Set as default view
          </label>
          {shareEnabled ? (
            <>
              <label className="flex items-center gap-2 text-[12px]">
                <input type="checkbox" checked={isShared} onChange={(event) => setIsShared(event.target.checked)} className="h-3.5 w-3.5 accent-primary" />
                Share with program users
              </label>
              {isShared ? (
                <div>
                  <label className="mb-1 block text-[11px] text-muted-foreground">Role restriction (optional)</label>
                  <input
                    value={roleCode}
                    onChange={(event) => setRoleCode(event.target.value)}
                    className="w-full rounded-md border border-border px-2 py-1.5 text-[12px] outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    placeholder="Role code, e.g. CCM"
                  />
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </AppModalBody>

      <AppModalFooter className="gap-2">
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button size="sm" className="h-7 text-xs" onClick={handleSave} disabled={!name.trim()}>
          Save view
        </Button>
      </AppModalFooter>
    </AppModal>
  );
}

export function GridViewsDropdown({
  views,
  onApplyView,
  onSaveView,
  onDeleteView,
  onSetDefault,
  className,
  shareEnabled = false,
}: GridViewsDropdownProps) {
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const privateViews = useMemo(() => views.filter((view) => !view.isShared), [views]);
  const sharedViews = useMemo(() => views.filter((view) => view.isShared), [views]);

  const renderItems = (items: SavedGridView[]) => items.map((view) => (
    <DropdownMenuItem
      key={view.id}
      className="group/item flex cursor-pointer items-center justify-between gap-2 py-1.5 text-xs"
      onSelect={() => onApplyView(view)}
    >
      <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate">
        {view.isDefault ? (
          <BookmarkCheck className="h-3 w-3 flex-shrink-0 text-primary" />
        ) : view.isShared ? (
          <Share2 className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
        ) : (
          <Bookmark className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
        )}
        <span className="truncate">{view.name}</span>
      </span>
      <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover/item:opacity-100">
        {!view.isDefault ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              void onSetDefault(view.id);
            }}
            className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-primary"
            aria-label="Set as default"
          >
            <Star className="h-2.5 w-2.5" />
          </button>
        ) : null}
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            void onDeleteView(view.id);
          }}
          className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-destructive"
          aria-label="Delete view"
        >
          <Trash2 className="h-2.5 w-2.5" />
        </button>
      </div>
    </DropdownMenuItem>
  ));

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className={cn('h-7 gap-1.5 text-xs', className)}>
            <Bookmark className="h-3 w-3" />
            Views
            {views.length > 0 ? (
              <span className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
                {views.length}
              </span>
            ) : null}
            <ChevronDown className="ml-0.5 h-3 w-3 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60 text-xs">
          {privateViews.length > 0 ? (
            <>
              <DropdownMenuLabel className="text-[11px] text-muted-foreground">My views</DropdownMenuLabel>
              {renderItems(privateViews)}
            </>
          ) : null}

          {sharedViews.length > 0 ? (
            <>
              {privateViews.length > 0 ? <DropdownMenuSeparator /> : null}
              <DropdownMenuLabel className="text-[11px] text-muted-foreground">Shared views</DropdownMenuLabel>
              {renderItems(sharedViews)}
            </>
          ) : null}

          {!views.length ? (
            <div className="px-2 py-2 text-[11px] text-muted-foreground">
              No saved views yet. Save your current layout to reuse it later.
            </div>
          ) : null}

          <DropdownMenuSeparator />
          <DropdownMenuItem className="cursor-pointer py-1.5 text-xs text-primary" onSelect={() => setShowSaveDialog(true)}>
            Save current view…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <SaveGridViewDialog
        open={showSaveDialog}
        onOpenChange={setShowSaveDialog}
        onSave={onSaveView}
        shareEnabled={shareEnabled}
      />
    </>
  );
}